import {
  ActualServer,
  API_BASE_URL,
  OverspendApplyResult,
  OverspendApplyTransfer,
  OverspendSuggestionResult
} from '../types';
import { auth } from './firebase';

interface OverspendRequestBase {
  server: ActualServer;
  budgetId: string;
  budgetPassword?: string;
  month?: string;
}

type ApiError = Error & { code?: string; errors?: string[] };

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
}

function buildRequestBody(
  { server, budgetId, budgetPassword, month }: OverspendRequestBase
): Record<string, unknown> {
  return {
    serverURL: server.url,
    password: server.password,
    budgetId,
    budgetPassword,
    month
  };
}

function mapSuggestionResponse(data: Record<string, unknown>): OverspendSuggestionResult {
  return {
    suggestionId: data.suggestionId as string | undefined,
    month: data.month as string,
    overspentCategories: (data.overspentCategories as OverspendSuggestionResult['overspentCategories']) ?? [],
    availableSources: (data.availableSources as OverspendSuggestionResult['availableSources']) ?? [],
    plan: (data.plan as OverspendSuggestionResult['plan']) ?? { coverages: [] },
    aiUsed: Boolean(data.aiUsed),
    fromCache: Boolean(data.fromCache),
    reprompted: Boolean(data.reprompted)
  };
}

async function parseApiError(response: Response): Promise<ApiError> {
  let errorMessage = 'REQUEST_FAILED';
  let errorCode: string | undefined;
  let errors: string[] = [];

  try {
    const errorData = await response.json();
    if (errorData.error) {
      errorMessage = errorData.error;
    }
    if (errorData.code) {
      errorCode = errorData.code;
    }
    if (Array.isArray(errorData.errors)) {
      errors = errorData.errors;
    }
  } catch {
    // keep generic
  }

  const err = new Error(errorMessage) as ApiError;
  err.code = errorCode;
  if (errors.length > 0) {
    err.errors = errors;
  }
  return err;
}

export async function suggestOverspendCoverage(
  params: OverspendRequestBase
): Promise<OverspendSuggestionResult> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/suggest-overspend-coverage`, {
    method: 'POST',
    headers,
    body: JSON.stringify(buildRequestBody(params))
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const data = await response.json();
  return mapSuggestionResponse(data);
}

export async function repromptOverspendCoverage(
  params: OverspendRequestBase & { userFeedback: string }
): Promise<OverspendSuggestionResult> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/reprompt-overspend-coverage`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...buildRequestBody(params),
      userFeedback: params.userFeedback
    })
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const data = await response.json();
  return mapSuggestionResponse(data);
}

export async function dismissOverspendCoverage(
  params: OverspendRequestBase
): Promise<{ success: boolean; dismissed: boolean }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dismiss-overspend-coverage`, {
    method: 'POST',
    headers,
    body: JSON.stringify(buildRequestBody(params))
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  return response.json();
}

export async function applyOverspendCoverage(
  params: OverspendRequestBase & { transfers: OverspendApplyTransfer[] }
): Promise<OverspendApplyResult> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/apply-overspend-coverage`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...buildRequestBody(params),
      transfers: params.transfers
    })
  });

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const data = await response.json();
  return {
    success: Boolean(data.success),
    applied: data.applied ?? 0,
    errors: data.errors ?? []
  };
}

export function isRateLimitError(error: unknown): boolean {
  return error instanceof Error
    && (error as ApiError).code === 'RATE_LIMIT_EXCEEDED';
}
